import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, MoreHorizontal } from 'lucide-react'
import { memo, useEffect, useMemo, useState } from 'react'
import { TableFrontendPagination } from '@/components/shared/table-frontend-pagination'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import * as Cells from '@/routes/registry-credentials/registry-credentials-table/cells'
import type {
  GetRegistryCredentialsQuery,
  GetRegistryCredentialsQueryVariables,
} from '@/routes/registry-credentials/registry-credentials-table/index.types.gen'
import type { Row } from '@/routes/registry-credentials/registry-credentials-table/types'

const GET_REGISTRY_CREDENTIALS = gql`
  query GetRegistryCredentials($idCompany: String!) {
    company(id: $idCompany) {
      id
      dockerRegistryCredentials {
        id
        name
        registry
        createdAt
      }
    }
  }
`

export const RegistryCredentialsTable = memo(
  ({ filterTerm }: { filterTerm: string }) => {
    const { idCompany } = useIdCompany()
    const { navigateToPath } = useCompanyPath()

    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

    const { data } = useSuspenseQuery<
      GetRegistryCredentialsQuery,
      GetRegistryCredentialsQueryVariables
    >(GET_REGISTRY_CREDENTIALS, {
      variables: {
        idCompany,
      },
    })
    const items = data?.company?.dockerRegistryCredentials ?? []

    const columns: ColumnDef<Row>[] = useMemo(
      () => [
        {
          accessorKey: 'name',
          size: 400,
          header: ({ column }) => (
            <Button
              className="cursor-pointer"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
            >
              Name{' '}
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="ml-2 size-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="ml-2 size-4" />
              ) : null}
            </Button>
          ),
          cell: Cells.NameCell,
        },
        {
          accessorKey: 'registry',
          size: 400,
          header: () => 'Registry',
          cell: Cells.RegistryCell,
        },
        {
          accessorKey: 'createdAt',
          size: 200,
          header: () => 'Created',
          cell: Cells.CreatedAtCell,
        },
        {
          id: 'actions',
          enableHiding: false,
          cell: ({ row }) => {
            return (
              <div className="text-right mr-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="cursor-pointer text-destructive"
                      onClick={() => {
                        navigateToPath({
                          pathname: `/registry-credentials/delete/:idRegistryCredential`,
                          variables: { idRegistryCredential: row.original.id },
                        })
                      }}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )
          },
        },
      ],
      []
    )

    const table = useReactTable({
      data: items,
      columns,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      state: {
        sorting,
        columnFilters,
      },
    })

    useEffect(() => {
      table.getColumn('name')?.setFilterValue(filterTerm ?? '')
    }, [table, filterTerm])

    return (
      <>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-muted/50 hover:bg-muted/50"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length
                  ? table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} className="cursor-pointer">
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            className="p-0 m-0"
                            key={cell.id}
                            style={{ maxWidth: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : null}
                {!table.getRowModel().rows?.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No registry credentials found.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between">
          <TableFrontendPagination table={table} />
        </CardFooter>
      </>
    )
  }
)
